import {isAlphanumeric, isLowerAlphanumeric} from "./isAlphanumeric";
import { SurveyQuestion } from "../../models";
import { BranchingRules } from "../Types";
import getBranchingRule from "./getBranchingRule";
import getQuestionContent from "./getQuestionContent";

export function checkIfAllValuesUndefined(value: Record<string, unknown>): boolean {
  let result = true;
  Object.keys(value).forEach( (key) => {
    if (value[key] !== undefined) {
      result = false;
    }
  });
  return result;
}

export interface TimeValidatorValue {
  hour: number;
  minute: number;
}

class ValidatorBase<T> {
  value!: T;
  errorMessage: string | undefined; // TODO make this string | null
  undefinedErrorMessage: string | undefined;

  noUndefined() {
    if (this.errorMessage === undefined) {
      if (this.value === undefined) {
        this.errorMessage = this.undefinedErrorMessage;
      }
    }
    return this;
  }

  noNull(message?: string) {
    if (this.errorMessage === undefined) {
      if (this.value === null) {
        this.errorMessage = message ?? 'Item cannot be empty'
      }
    }
    return this;
  }

  validate(): string | undefined {
    return this.errorMessage;
  }
}

/*
 Implements methods that check if a string matches certain conditions. Can be used
 with method chaining. You can either call validate() to get the message value at the end, if any, or
 directly access the `message` property.
 */
export class StringValidator extends ValidatorBase<string | undefined> {

  constructor(value: string | undefined, undefinedErrorMessage? : string) {
    super();
    this.value = value;
    this.undefinedErrorMessage = undefinedErrorMessage ?? 'Value cannot be undefined'
  }

  noEmpty(message?: string): StringValidator {
    if (this.errorMessage === undefined) {
      const errorMessage = message ? message : 'Value cannot be empty'
      this.errorMessage = this.value === "" ?  errorMessage : undefined;
    }
    return this
  }

  // Checks that the string is greater than or equal to the min
  min(count: number, message?: string): StringValidator {
    // Run check only if error not already detected
    this.noUndefined();
    if (this.errorMessage === undefined) {
      const errorMessage = message ? message : 'Error'
      this.errorMessage = this.value!.length >= count ? undefined : errorMessage;
    }
    return this
  }

  // Checks that the string is less than or equal to the max
  max(count: number, message?: string): StringValidator {
    this.noUndefined();
    if (this.errorMessage === undefined) {
      const errorMessage = message ? message : 'Error'
      this.errorMessage = this.value!.length <= count ? undefined : errorMessage;
    }
    return this
  }

  alphaNum(message?: string): StringValidator {
    this.noUndefined();
    if (this.errorMessage === undefined) {
      const errorMessage = message ? message : 'Value must only contain letters and numbers'
      this.errorMessage = isAlphanumeric(this.value!) ? undefined : errorMessage;
    }
    return this
  }

  lowercaseAlphaNum(message?: string): StringValidator {
    this.noUndefined();
    if (this.errorMessage === undefined) {
      const errorMessage = message ? message : 'Value must only contain letters and numbers'
      this.errorMessage = isLowerAlphanumeric(this.value!) ? undefined : errorMessage;
    }
    return this
  }

  /**
   * Checks if string consists of ONLY whitespace
   */
  noSpace(message?: string): StringValidator {
    this.noUndefined();
    if (this.errorMessage === undefined) {
      const errorMessage = message ? message : 'Value cannot contain spaces'
      this.errorMessage = this.value!.trim().length !== 0 ? undefined : errorMessage;
    }
    return this
  }

  /**
   * Checks if a value is unique
   * @param values: the array of strings to check for uniqueness in
   * @param message: the message to return if it fails. Defaults to `Item is a duplicate`
   * @param valueIndex: the index for the value if it is already in the array
   * @param ignoreCase: defaults to false, set to true if check should be case-insensitive
   *
   */
  unique(values: string[], message?: string, valueIndex?: number, ignoreCase: boolean = false): StringValidator {
    if (this.errorMessage === undefined) {
      const otherValues = ignoreCase ? values.map(item => item.toLowerCase()) : [...values];
      if (valueIndex !== undefined) {
        // Splicing should be preformat for the length of most arrays of user input values
        otherValues.splice(valueIndex,1);
      }
      const errorMessage = message ? message : 'Item is a duplicate'
      this.errorMessage = !otherValues.includes(ignoreCase ? this.value!.toLowerCase() : this.value!) ? undefined : errorMessage;
    }
    return this
  }
}

/*
 Implements methods that check if a string matches certain conditions. Can be used
 with method chaining to check multiple conditions. You can either call validate()
 to get the message value at the end, if any, or directly access the `message` property.
 */
export class NumberValidator extends ValidatorBase<number | undefined> {

  constructor(value: number | undefined, undefinedErrorMessage? : string) {
    super();
    this.value = value;
    this.undefinedErrorMessage = undefinedErrorMessage ?? 'Value cannot be undefined';
  }

  noNaN(message?: string) {
    this.noUndefined();
    if (this.errorMessage === undefined) {
      const errorMessage = message ? message : 'Value must contain at least one number'
      this.errorMessage = isNaN(this.value!) ? errorMessage : undefined;
    }
    return this
  }

  // Checks that the value is greater than or equal to the min
  min(size: number | undefined, message?: string): NumberValidator {
    this.noUndefined();
    // Run check only if error not already detected
    if (this.errorMessage === undefined && size !== undefined) {
      const errorMessage = message ? message : 'Error'
      this.errorMessage = this.value! >= size ? undefined : errorMessage;
    }
    return this
  }

  /**
   * Checks that the value is less than or equal to the max
   */
  max(size: number | undefined, message?: string): NumberValidator {
    this.noUndefined();
    // Run check only if error not already detected and size is correct
    if (this.errorMessage === undefined && size !== undefined) {
      const errorMessage = message ? message : 'Error'
      this.errorMessage = this.value! <= size ? undefined : errorMessage;
    }
    return this
  }

  /**
   * Checks that the value is strictly less than the size given
   */
  strictlyLessThan(size: number | undefined, message?: string): NumberValidator {
    this.noUndefined();
    // Run check only if error not already detected and size is correct
    if (this.errorMessage === undefined && size !== undefined) {
      const errorMessage = message ? message : `Value must be less than ${size}`
      this.errorMessage = this.value! < size ? undefined : errorMessage;
    }
    return this
  }

  /**
   * Checks that the value is strictly greater than the size given
   */
  strictlyGreaterThan(size: number | undefined, message?: string): NumberValidator {
    this.noUndefined();
    // Run check only if error not already detected and size is correct
    if (this.errorMessage === undefined && size !== undefined) {
      const errorMessage = message ? message : `Value must be greater than ${size}`
      this.errorMessage = this.value! > size ? undefined : errorMessage;
    }
    return this
  }

}

export class TimeValidator extends ValidatorBase<TimeValidatorValue> {

  constructor(value: TimeValidatorValue, undefinedErrorMessage?: string) {
    super();
    this.value = value;
    this.undefinedErrorMessage = undefinedErrorMessage ?? 'Value cannot be undefined';
  }

  noNaN(message?: string) {
    this.noUndefined();
    if (this.errorMessage === undefined) {
      const errorMessage = message ? message : 'Value must contain at least one number'
      this.errorMessage = isNaN(this.value.hour) || isNaN(this.value.minute) ? errorMessage : undefined;
    }
    return this
  }

  // Checks that the value is greater than or equal to the min
  min(hour: number | undefined, minute: number | undefined, message?: string): TimeValidator  {
    this.noUndefined();
    // Run check only if error not already detected
    if (this.errorMessage === undefined && hour !== undefined && minute !== undefined) {
      const errorMessage = message ? message : 'Error'
      if (this.value.hour > hour || ( this.value.hour === hour && this.value.minute >= minute)) {
        this.errorMessage = undefined;
      } else {
        this.errorMessage = errorMessage;
      }
    }
    return this
  }

  /**
   * Checks that the value is less than or equal to the max
   */
  max(hour: number | undefined, minute: number | undefined,  message?: string): TimeValidator {
    this.noUndefined();
    // Run check only if error not already detected and size is correct
    if (this.errorMessage === undefined && hour !== undefined && minute !== undefined) {
      const errorMessage = message ? message : 'Error'
      if (this.value.hour < hour || ( this.value.hour === hour && this.value.minute <= minute)) {
        this.errorMessage = undefined;
      } else {
        this.errorMessage = errorMessage;
      }
    }
    return this
  }

  /**
   * Checks that the value is strictly less than the size given
   */
  strictlyLessThan(hour: number | undefined, minute: number | undefined,  message?: string): TimeValidator  {
    this.noUndefined();
    // Run check only if error not already detected and size is correct
    if (this.errorMessage === undefined && hour !== undefined && minute !== undefined) {
      const errorMessage = message ? message : 'Error'
      if (this.value.hour < hour || ( this.value.hour === hour && this.value.minute < minute)) {
        this.errorMessage = undefined;
      } else {
        this.errorMessage = errorMessage;
      }
    }
    return this
  }

  /**
   * Checks that the value is strictly greater than the size given
   */
  strictlyGreaterThan(hour: number | undefined, minute: number | undefined,  message?: string): TimeValidator  {
    this.noUndefined();
    // Run check only if error not already detected and size is correct
    if (this.errorMessage === undefined && hour !== undefined && minute !== undefined) {
      const errorMessage = message ? message : 'Error'
      if (this.value.hour > hour || ( this.value.hour === hour && this.value.minute > minute)) {
        this.errorMessage = undefined;
      } else {
        this.errorMessage = errorMessage;
      }
    }
    return this
  }

}

type PerChoiceDestination = {
  choiceId: string;
  destinationQuestionId: string;
};

type PerChoiceBranchingRule = BranchingRules & {
  destinations: (PerChoiceDestination | null)[];
};

const isPerChoiceBranchingRule = (
  branchingRule: BranchingRules
): branchingRule is PerChoiceBranchingRule =>
  "destinations" in branchingRule && branchingRule.destinations != null;

const isValidFutureDestination = (
  destinationQuestionId: string | null | undefined,
  questionIndex: number,
  questions: (SurveyQuestion | null)[]
) =>
  destinationQuestionId === "completionPage" ||
  questions
    .slice(questionIndex + 1)
    .some(question => destinationQuestionId === getQuestionContent(question)?.questionId);

const hasCompletePerChoiceRule = (
  question: SurveyQuestion,
  branchingRule: PerChoiceBranchingRule,
  questionIndex: number,
  questions: (SurveyQuestion | null)[]
) => {
  const choices = question.choice?.textChoices ?? [];
  if (question.choice?.multipleChoice !== false || choices.length === 0) {
    return false;
  }

  if (!isValidFutureDestination(branchingRule.defaultQuestionId, questionIndex, questions)) {
    return false;
  }

  const choiceIDs = choices.map(choice => choice?.textChoiceID);
  const validChoiceIDs = choiceIDs.filter(
    (choiceID): choiceID is string => !!choiceID
  );
  const hasUniqueChoiceIDs = new Set(validChoiceIDs).size === validChoiceIDs.length;
  if (validChoiceIDs.length !== choices.length || !hasUniqueChoiceIDs) return false;

  const destinations = branchingRule.destinations;

  const destinationChoiceIDs = destinations
    .filter((destination): destination is PerChoiceDestination => destination !== null)
    .map(destination => destination.choiceId);
  const hasUniqueDestinationChoiceIDs =
    new Set(destinationChoiceIDs).size === destinationChoiceIDs.length;
  if (!hasUniqueDestinationChoiceIDs) return false;

  const destinationsAreValid = destinations.every(destination => {
    if (
      !destination ||
      !destination.choiceId ||
      !validChoiceIDs.includes(destination.choiceId) ||
      !destination.destinationQuestionId
    ) {
      return false;
    }

    return isValidFutureDestination(
      destination.destinationQuestionId,
      questionIndex,
      questions
    );
  });
  if (!destinationsAreValid) return false;

  return true;
};

export class BranchingValidator extends ValidatorBase<(SurveyQuestion | null)[]> {

  constructor(value: (SurveyQuestion | null)[]) {
    super();
    this.value = value;
  }

  ordered() {
    const orderValidationMessage = "Please check branching rules and re-save";
    const questions = this.value;

    questions.forEach((question, index) => {
      if (!question) return;

      const branchingRule = getBranchingRule(question);
      if (!branchingRule) return;

      if (isPerChoiceBranchingRule(branchingRule)) {
        if (!hasCompletePerChoiceRule(question, branchingRule, index, questions)) {
          this.errorMessage = orderValidationMessage;
        }
        return;
      }

      if (!isValidFutureDestination(branchingRule.defaultQuestionId, index, questions)) {
        this.errorMessage = orderValidationMessage;
      }

      if (
        branchingRule.destinationQuestionId !== "" &&
        !isValidFutureDestination(branchingRule.destinationQuestionId, index, questions)
      ) {
        this.errorMessage = orderValidationMessage;
      }
    });
    return this;
  }

}